// formSessionStore.js
//
// WHY THIS EXISTS
// ────────────────────────────────────────────────────────────────────────
// fittingSessionStore.js fixed the RUN (loading/result/Stop) surviving
// navigation away from and back to /data-fitting. It didn't touch the
// input fields themselves, so a real gap remained: react-router still
// unmounts DataFittingPage on every route change, and plain `useState`
// dies with the component — so reactants/products, catalyst weight,
// reactor volume, the uploaded file, model-input toggles, all of it reset
// to their hardcoded defaults the moment you left and came back, even
// though nothing about the fit (or the user's intent) actually changed.
//
// Fix: the same pattern as fittingSessionStore.js — a plain JS object
// living in this module, outside the React tree, mirroring each field's
// latest value. `usePersistedState(key, initial)` is a drop-in
// replacement for `useState(initial)`: same [value, setValue] return
// shape, so every existing call site keeps working unchanged. The only
// difference from useState is that on mount it reads its last known value
// from this module instead of always starting from `initial`, and it
// writes every change back so the next mount (after any navigation) picks
// up exactly where the user left off.
//
// This is intentionally a live mirror of ALL Data Input + Model Input
// fields, not just the ones belonging to an in-flight run — the request
// was "don't lose what I typed when I switch pages," full stop, not
// "only preserve it while a fit happens to be running."
//
// Cleared automatically on Reset, because Reset does a full
// `window.location.reload()` (see DataFittingPage.jsx), which wipes this
// module along with everything else in the tab's JS memory. No extra
// wiring needed for that path.

import { useState, useEffect } from 'react';

let formState = {};

export function getFormValue(key, fallback) {
  return Object.prototype.hasOwnProperty.call(formState, key) ? formState[key] : fallback;
}

export function setFormValue(key, value) {
  formState[key] = value;
}

export function clearFormState() {
  formState = {};
}

// Drop-in useState replacement backed by the module-level mirror above.
export function usePersistedState(key, initialValue) {
  const [value, setValue] = useState(() => getFormValue(key, initialValue));
  useEffect(() => { setFormValue(key, value); }, [key, value]);
  return [value, setValue];
}